import { useMemo } from 'react'
import { Contract } from '@ethersproject/contracts'
import { ChainId, WETH } from 'sdk'
import useWeb3Provider from 'hooks/useWeb3Provider'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import {
  getBep20Contract,
  getDmdContract,
  getMasterchefContract,
  getPresaleContract,
  getDarkVaultContract,
  getDmdVaultContract,
  getMultiPoolContract,
  getNftContract,
  getNftPoolContract,
  getStrategyContract,
  getZapContract,
  getLotteryContract,
  getNftMultiTransferContract,
  getNftLaunchpadFactoryContract,
  getNftLaunchpadFactoryV2Contract,
  getNftV2Contract,
} from 'utils/contractHelpers'
import { Address } from 'config/constants/types'
import { Series } from 'state/types'
import ENS_ABI from '../config/abi/ens-registrar.json'
import ENS_PUBLIC_RESOLVER_ABI from '../config/abi/ens-public-resolver.json'
import ERC20_ABI from '../config/abi/erc20.json'
import WETH_ABI from '../config/abi/weth.json'
import { ERC20_BYTES32_ABI } from '../config/abi/erc20'
import { MULTICALL_ABI, MULTICALL_NETWORKS } from '../config/constants/multicall'
import { getContract } from '../utils'

/**
 * Helper hooks to get specific contracts (by ABI)
 */

export const useERC20 = (address: string) => {
  const provider = useWeb3Provider()
  return useMemo(() => getBep20Contract(address, provider.getSigner()), [address, provider])
}

/**
 * @see https://docs.openzeppelin.com/contracts/3.x/api/token/erc721
 */
export const useDmd = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getDmdContract(provider.getSigner()), [provider])
}

export const useMasterchef = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getMasterchefContract(provider.getSigner()), [provider])
}

export const useNftLaunchpadFactoryV2 = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getNftLaunchpadFactoryV2Contract(provider.getSigner()), [provider])
}
export const useNftLaunchpadFactory = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getNftLaunchpadFactoryContract(provider.getSigner()), [provider])
}

export const usePresale = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getPresaleContract(provider.getSigner()), [provider])
}

export const useMultiPoolContract = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getMultiPoolContract(provider.getSigner()), [provider])
}

export const useNftMultiTransferContract = () => {
  const provider = useWeb3Provider()
  return useMemo(() => getNftMultiTransferContract(provider.getSigner()), [provider])
}

export const useDarkvault = (address: Address) => {
  const provider = useWeb3Provider()

  return useMemo(() => getDarkVaultContract(address, provider.getSigner()), [provider, address])
}

export const useStrategy = (address: Address) => {
  const provider = useWeb3Provider()

  return useMemo(() => getStrategyContract(address, provider.getSigner()), [provider, address])
}

export const useDmdvault = () => {
  const provider = useWeb3Provider()

  return useMemo(() => getDmdVaultContract(provider.getSigner()), [provider])
}

export const useNftContract = (address: Address) => {
  const provider = useWeb3Provider()

  return useMemo(() => getNftContract(address, provider.getSigner()), [address, provider])
}

export const useNftV2Contract = (address: Address) => {
  const provider = useWeb3Provider()

  return useMemo(() => getNftV2Contract(address, provider.getSigner()), [address, provider])
}

export const useNftPoolContract = (series: Series) => {
  const provider = useWeb3Provider()

  return useMemo(() => getNftPoolContract(series, provider.getSigner()), [series, provider])
}

export const useZapContract = () => {
  const provider = useWeb3Provider()

  return useMemo(() => getZapContract(provider.getSigner()), [provider])
}

export const useLotteryContract = () => {
  const provider = useWeb3Provider()

  return useMemo(() => getLotteryContract(provider.getSigner()), [provider])
}

// Code below migrated from Exchange useContract.ts

// returns null on errors
function useContract(address: string | undefined, ABI: any, withSignerIfPossible = true): Contract | null {
  const { library, account } = useActiveWeb3React()

  return useMemo(() => {
    if (!address || !ABI || !library) return null
    try {
      return getContract(address, ABI, library, withSignerIfPossible && account ? account : undefined)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, ABI, library, withSignerIfPossible, account])
}

export function useWETHContract(withSignerIfPossible?: boolean): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? WETH[chainId].address : undefined, WETH_ABI, withSignerIfPossible)
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_ABI, withSignerIfPossible)
}

export function useENSRegistrarContract(withSignerIfPossible?: boolean): Contract | null {
  const { chainId } = useActiveWeb3React()
  let address: string | undefined
  if (chainId) {
    // eslint-disable-next-line default-case
    switch (chainId) {
      case ChainId.MAINNET:
      case ChainId.TESTNET:
        address = '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e'
        break
    }
  }
  return useContract(address, ENS_ABI, withSignerIfPossible)
}

export function useENSResolverContract(address: string | undefined, withSignerIfPossible?: boolean): Contract | null {
  return useContract(address, ENS_PUBLIC_RESOLVER_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
}

export function useMulticallContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId && MULTICALL_NETWORKS[chainId], MULTICALL_ABI, false)
}
